// One page fetch, done one way (#1291). Every page controller used to hand-roll
// its own fetch, error, 404, retry and first-load tracking, four to six
// different ways, and most had no stale guard: a slow response for an old
// `:id`, sport, scope or query landed after the new one and overwrote it.
//
// usePageResource(load, deps):
// - runs `load` on mount and whenever `deps` change or `retry()` is called;
// - drops every result but the latest request's (stale guard; unmount too);
// - clears the error and notFound at the start of every load, so a good
//   sport switch or navigation never keeps the last failure's error page;
// - `error` is the one friendly line (#420), never the raw "API error: …";
// - `notFoundOn404: true` (detail pages) turns a 404 into `notFound`, a typed
//   check (services/api errors carry `status`). List pages leave it off: a
//   404 there (e.g. no league for this host) is an error with a retry, since
//   their views have no not-found state;
// - by default keeps the last good data while a refetch runs (lists:
//   `refreshing`); `keepStale: false` clears it when `deps` change (detail
//   pages, so a new `:id` shows the loader, not the previous entity), but a
//   retry or poll of the same entity keeps it;
// - `enabled: false` holds the load (e.g. until the sport resolves, #1164):
//   nothing is fetched and the page stays `loading`.
//
// pageStatus() is the one status rule: notFound, then loading (no data yet),
// then error, then ready; a settled load with no data (null or undefined) is
// notFound.

import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react'
import { useLabels } from '../labels'
import type { PageStatusKind } from '../template/pages'

/** A typed 404: services/api errors carry `status`. */
export function isNotFound(err: unknown): boolean {
  return (err as { status?: unknown } | null)?.status === 404
}

export interface PageStatusInput {
  loading: boolean
  hasData: boolean
  error: string | null
  notFound: boolean
}

export function pageStatus({ loading, hasData, error, notFound }: PageStatusInput): PageStatusKind {
  if (notFound) return 'notFound'
  if (loading && !hasData) return 'loading'
  if (error) return 'error'
  return hasData ? 'ready' : loading ? 'loading' : 'notFound'
}

export interface PageResource<T> {
  /** The latest good result (kept across refetches unless `keepStale: false`); null before the first. */
  data: T | null
  /** A load is in flight. */
  loading: boolean
  /** The friendly error line for the latest load (#420), or null. */
  error: string | null
  /** The latest load failed with a 404 (only with `notFoundOn404`). */
  notFound: boolean
  status: PageStatusKind
  /** A refetch with data already on screen. */
  refreshing: boolean
  /** Really refetches (#414). */
  retry: () => void
  /** Hides the current error without refetching (a dismissable refresh-error banner). */
  dismissError: () => void
}

export interface PageResourceOptions {
  /** Keep the last good data while a new key loads (default true). Retries always keep it. */
  keepStale?: boolean
  /** False holds the load (status stays `loading`); it runs once this turns true. Default true. */
  enabled?: boolean
  /** The page's own friendly error line; defaults to the shared one. */
  fallback?: string
  /** A 404 is `notFound` (detail pages); off, it is an error like any other (lists). Default false. */
  notFoundOn404?: boolean
}

export function usePageResource<T>(load: () => Promise<T>, deps: DependencyList, opts: PageResourceOptions = {}): PageResource<T> {
  const { keepStale = true, enabled = true, fallback, notFoundOn404 = false } = opts
  const t = useLabels()
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const lastAttempt = useRef(attempt)

  useEffect(() => {
    const isRetry = attempt !== lastAttempt.current
    lastAttempt.current = attempt
    if (!enabled) return
    let live = true
    setLoading(true)
    setError(null)
    setNotFound(false)
    if (!keepStale && !isRetry) setData(null)
    load().then(
      (value) => {
        if (!live) return
        setData(value)
        setLoading(false)
      },
      (err: unknown) => {
        if (!live) return
        setError(t.ui.common.errorMessage(err, fallback ?? t.ui.common.loadError))
        setNotFound(notFoundOn404 && isNotFound(err))
        setLoading(false)
      },
    )
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the caller's deps decide when to refetch
  }, [...deps, enabled, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  const dismissError = useCallback(() => setError(null), [])
  const hasData = data != null
  return {
    data,
    loading,
    error,
    notFound,
    status: pageStatus({ loading, hasData, error, notFound }),
    refreshing: loading && hasData,
    retry,
    dismissError,
  }
}
